import * as THREE from 'three';
import type { Assets } from '../core/Assets';
import type { Decision, DocSpec, Encounter } from '../game/data/types';
import { docWorldSize, renderDoc } from './DocCanvas';
import { buildStampCanvas, STAMP_ART_W, STAMP_ART_H } from './StampArt';

export interface InspectSelection {
  source: string;
  label: string;
  value: string;
  tag?: string;
  bad?: boolean;
  clientX: number;
  clientY: number;
}

export interface DeskCallbacks {
  onFieldSelected(sel: InspectSelection): void;
  onStamped(decision: Decision): void;
  onFolderOpened(): void;
  onWeighed(actual: number): void;
  onCharacterClicked(sel: InspectSelection): void;
  sfx(id: string): void;
}

interface Tween {
  update(dt: number): boolean;
}

function tweenTo(
  target: { [k: string]: number } | THREE.Vector3,
  props: Record<string, number>,
  dur: number,
  onDone?: () => void,
): Tween {
  const from: Record<string, number> = {};
  for (const k of Object.keys(props)) from[k] = (target as Record<string, number>)[k];
  let t = 0;
  return {
    update(dt: number): boolean {
      t += dt;
      const p = Math.min(1, t / dur);
      const e = 1 - (1 - p) * (1 - p) * (1 - p);
      for (const k of Object.keys(props)) {
        (target as Record<string, number>)[k] = from[k] + (props[k] - from[k]) * e;
      }
      if (p >= 1) {
        onDone?.();
        return false;
      }
      return true;
    },
  };
}

interface DocMesh {
  spec: DocSpec;
  mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  canvasW: number;
  canvasH: number;
  fieldRects: ReturnType<typeof renderDoc>['fieldRects'];
  sealRect: ReturnType<typeof renderDoc>['sealRect'];
  worldW: number;
  worldH: number;
  isSlip: boolean;
}

const STAGE_W = 1600;
const STAGE_H = 900;
const DESK_TOP = 66;
/** Resting Y for the counter-window group (character + frame). Shifted down from center
 *  so the 10%-enlarged frame grows into the desk's space rather than clipping the camera top. */
const CHAR_Y = 240;

export class DeskScene {
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly pointer = new THREE.Vector2();
  private tweens: Tween[] = [];
  private docs: DocMesh[] = [];
  private character: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | null = null;
  private characterPortrait = '';
  private characterName = '';
  private folder: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | null = null;
  private folderOpen = false;
  private slip: DocMesh | null = null;
  private stampGreen!: THREE.Group;
  private stampRed!: THREE.Group;
  private scaleMesh: THREE.Mesh | null = null;
  private scaleReadout: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | null = null;
  private weighed = false;
  private coinWeight: { claimed: number; actual: number } | null = null;
  private dragging: DocMesh | null = null;
  private dragOffset = new THREE.Vector2();
  private dragMoved = false;
  private zTop = 1;
  stampsEnabled = false;
  private stamped = false;
  private shakeT = 0;

  constructor(
    canvas: HTMLCanvasElement,
    private readonly assets: Assets,
    private readonly cb: DeskCallbacks,
  ) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor(0x0d0f12);
    this.camera = new THREE.OrthographicCamera(-800, 800, 450, -450, 0.1, 100);
    this.camera.position.z = 10;
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    canvas.style.cursor = 'grab';
  }

  private plane(
    w: number,
    h: number,
    texKey: string | null,
    color = 0xffffff,
    keyed = false,
  ): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> {
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true });
    if (texKey) {
      const tex = keyed ? this.assets.textureKeyed(texKey) : this.assets.texture(texKey);
      if (tex.image) mat.map = tex;
      else mat.color = new THREE.Color(0x2a2e34);
    }
    return new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
  }

  /** Call once assets have loaded. Textures resolve at build time. */
  initVisuals(): void {
    const bg = this.plane(2400, 1350, 'scene:bg-office');
    bg.position.set(0, 135, -20);
    this.scene.add(bg);

    const glassTint = this.plane(2400, 800, null, 0x101418);
    glassTint.material.opacity = 0.18;
    glassTint.position.set(0, 320, -14);
    this.scene.add(glassTint);

    const desk = this.plane(2400, 620, 'prop:desk-texture');
    desk.position.set(0, DESK_TOP - 310, -10);
    this.scene.add(desk);
    const deskEdge = this.plane(2400, 14, null, 0x14110c);
    deskEdge.material.opacity = 0.85;
    deskEdge.position.set(0, DESK_TOP, -9.9);
    this.scene.add(deskEdge);

    // Scale fixture, left side of the desk.
    this.scaleMesh = this.plane(240, 240, 'prop:scale', 0xffffff, true);
    this.scaleMesh.position.set(-580, -240, -8);
    this.scene.add(this.scaleMesh);

    this.stampGreen = this.buildStamp('greenlit');
    this.stampGreen.position.set(300, 40, 2);
    this.scene.add(this.stampGreen);
    this.stampRed = this.buildStamp('sunset');
    this.stampRed.position.set(460, 40, 2);
    this.scene.add(this.stampRed);
  }

  private buildStamp(kind: Decision): THREE.Group {
    const group = new THREE.Group();
    const canvas = buildStampCanvas(kind);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    const worldW = 140;
    const worldH = worldW * (STAMP_ART_H / STAMP_ART_W);
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(worldW, worldH),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true }),
    );
    group.add(mesh);
    group.userData = { stamp: kind };
    return group;
  }

  private textSprite(
    text: string,
    w: number,
    h: number,
    color: string,
    size: number,
  ): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> {
    const canvas = document.createElement('canvas');
    canvas.width = w * 2;
    canvas.height = h * 2;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = color;
    ctx.font = `bold ${size * 2}px Courier New`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, w, h);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true }),
    );
    return mesh;
  }

  // ---------------------------------------------------------------- encounter
  presentEncounter(enc: Encounter): void {
    this.clearEncounter();
    this.characterPortrait = enc.portrait;
    this.characterName = enc.name;
    this.coinWeight = enc.coinWeight ?? null;
    this.character = this.plane(363, 418, `portrait:${enc.portrait}`);
    this.character.position.set(-1100, CHAR_Y, -16);
    this.scene.add(this.character);
    // Counter-window frame around the visitor.
    const frame = this.plane(403, 458, null, 0x161a1f);
    frame.position.set(0, 0, -0.1);
    const frameLip = this.plane(429, 484, null, 0x3a3428);
    frameLip.position.set(0, 0, -0.2);
    this.character.add(frame, frameLip);
    this.tweens.push(tweenTo(this.character.position, { x: -250 }, 0.7));
    this.cb.sfx('paperSlide1');

    if (!enc.noStamp && enc.docs.length > 0) {
      this.folder = this.plane(240, 180, 'prop:folder', 0xffffff, true);
      this.folder.position.set(-250, 700, 1);
      this.folder.userData = { folder: true };
      this.scene.add(this.folder);
      this.tweens.push(tweenTo(this.folder.position, { y: -60 }, 0.7));
      this.folderOpen = false;
    }
    this.stamped = false;
    this.stampsEnabled = false;
    this.weighed = false;
    this.setStampVisual();
  }

  spawnDocs(enc: Encounter): void {
    if (this.folder) {
      this.scene.remove(this.folder);
      disposeMesh(this.folder);
      this.folder = null;
    }
    this.folderOpen = true;
    const positions: Array<[number, number]> = [
      [-420, -160], [-120, -140], [160, -200], [-280, -320], [40, -330], [300, -80],
    ];
    enc.docs.forEach((spec, i) => {
      const doc = this.makeDoc(spec, false);
      const [tx, ty] = positions[i % positions.length];
      doc.mesh.position.set(-250, 40, this.zTop += 0.01);
      this.scene.add(doc.mesh);
      this.docs.push(doc);
      this.tweens.push(tweenTo(doc.mesh.position, { x: tx + (i > 5 ? 30 : 0), y: ty }, 0.5 + i * 0.08));
      doc.mesh.rotation.z = (Math.random() - 0.5) * 0.06;
    });
    // Decision slip
    const slipSpec: DocSpec = {
      id: '__slip', kind: 'slip', title: 'DECISION SLIP',
      subtitle: `APPLICANT: ${enc.name.toUpperCase()}`,
      fields: [
        { id: 'rq', label: 'REQUEST', value: enc.role.toUpperCase().slice(0, 30) },
        { id: 'dt', label: 'PROCESSED BY', value: 'DESK 7' },
      ],
    };
    const slip = this.makeDoc(slipSpec, true);
    slip.mesh.position.set(-250, 40, this.zTop += 0.01);
    this.scene.add(slip.mesh);
    this.docs.push(slip);
    this.slip = slip;
    this.tweens.push(tweenTo(slip.mesh.position, { x: 380, y: -290 }, 0.6));
    this.stampsEnabled = true;
    this.setStampVisual();
    this.cb.sfx('paperSlide3');
    this.cb.onFolderOpened();
  }

  private makeDoc(spec: DocSpec, isSlip: boolean): DocMesh {
    const render = renderDoc(this.assets, spec);
    const [worldW, worldH] = docWorldSize(spec);
    const tex = new THREE.CanvasTexture(render.canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(worldW, worldH),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true }),
    );
    return {
      spec,
      mesh,
      canvasW: render.canvas.width,
      canvasH: render.canvas.height,
      fieldRects: render.fieldRects,
      sealRect: render.sealRect,
      worldW,
      worldH,
      isSlip,
    };
  }

  stampSlip(decision: Decision): void {
    if (!this.slip || this.stamped) return;
    this.stamped = true;
    this.stampsEnabled = false;
    const stampGroup = decision === 'greenlit' ? this.stampGreen : this.stampRed;
    const home = stampGroup.position.clone();
    const target = this.slip.mesh.position;
    this.tweens.push(
      tweenTo(stampGroup.position, { x: target.x, y: target.y - 40, z: 6 }, 0.22, () => {
        this.cb.sfx('stamp');
        this.shakeT = 0.25;
        const render = renderDoc(this.assets, this.slip!.spec, decision);
        (this.slip!.mesh.material.map as THREE.CanvasTexture).image = render.canvas;
        (this.slip!.mesh.material.map as THREE.CanvasTexture).needsUpdate = true;
        this.tweens.push(
          tweenTo(stampGroup.position, { x: home.x, y: home.y, z: home.z }, 0.3, () => {
            this.cb.onStamped(decision);
          }),
        );
      }),
    );
  }

  returnDocs(onDone: () => void): void {
    for (const doc of this.docs) {
      this.tweens.push(tweenTo(doc.mesh.position, { x: -250, y: 700 }, 0.5));
    }
    if (this.character) {
      const char = this.character;
      this.tweens.push(tweenTo(char.position, { x: -1100 }, 0.6, onDone));
    } else {
      onDone();
    }
    this.cb.sfx('paperSlide3');
  }

  clearEncounter(): void {
    for (const doc of this.docs) {
      this.scene.remove(doc.mesh);
      disposeMesh(doc.mesh);
    }
    this.docs = [];
    this.slip = null;
    if (this.character) {
      this.scene.remove(this.character);
      disposeMesh(this.character);
      this.character = null;
    }
    if (this.folder) {
      this.scene.remove(this.folder);
      disposeMesh(this.folder);
      this.folder = null;
    }
    this.clearReadout();
    this.stampsEnabled = false;
    this.stamped = false;
    this.setStampVisual();
  }

  private clearReadout(): void {
    if (this.scaleReadout) {
      this.scene.remove(this.scaleReadout);
      disposeMesh(this.scaleReadout);
      this.scaleReadout = null;
    }
  }

  private setStampVisual(): void {
    if (!this.stampGreen) return;
    const on = this.stampsEnabled;
    for (const g of [this.stampGreen, this.stampRed]) {
      g.traverse((o) => {
        const mesh = o as THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
        if (mesh.isMesh) mesh.material.opacity = on ? 1 : 0.45;
      });
    }
  }

  // ---------------------------------------------------------------- input
  private eventToWorld(e: PointerEvent): THREE.Vector2 {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    return new THREE.Vector2(
      this.pointer.x * (this.camera.right - this.camera.left) / 2,
      this.pointer.y * (this.camera.top - this.camera.bottom) / 2,
    );
  }

  private topDocAt(world: THREE.Vector2): DocMesh | null {
    let best: DocMesh | null = null;
    for (const doc of this.docs) {
      const { x, y } = doc.mesh.position;
      if (
        world.x >= x - doc.worldW / 2 && world.x <= x + doc.worldW / 2 &&
        world.y >= y - doc.worldH / 2 && world.y <= y + doc.worldH / 2
      ) {
        if (!best || doc.mesh.position.z > best.mesh.position.z) best = doc;
      }
    }
    return best;
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    const world = this.eventToWorld(e);
    this.dragMoved = false;

    // Stamps
    if (this.stampsEnabled) {
      for (const g of [this.stampGreen, this.stampRed]) {
        if (Math.abs(world.x - g.position.x) < 64 && Math.abs(world.y - g.position.y) < 98) {
          this.stampSlip(g.userData.stamp as Decision);
          return;
        }
      }
    }

    // Folder
    if (this.folder && !this.folderOpen &&
        Math.abs(world.x - this.folder.position.x) < 130 &&
        Math.abs(world.y - this.folder.position.y) < 100) {
      this.cb.sfx('click');
      this.folder.userData.wantsOpen = true;
      return;
    }

    // Character (inspect face)
    if (this.character &&
        Math.abs(world.x - this.character.position.x) < 182 &&
        Math.abs(world.y - this.character.position.y) < 209) {
      this.cb.onCharacterClicked({
        source: 'COUNTER', label: 'PERSON PRESENT', value: this.characterPortrait,
        tag: 'face', clientX: e.clientX, clientY: e.clientY,
      });
      return;
    }

    // Scale readout inspect
    if (this.scaleReadout && this.coinWeight &&
        Math.abs(world.x - this.scaleReadout.position.x) < 130 &&
        Math.abs(world.y - this.scaleReadout.position.y) < 30) {
      const { claimed, actual } = this.coinWeight;
      const mismatched = Math.abs(claimed - actual) / Math.max(claimed, actual) > 0.15;
      this.cb.onFieldSelected({
        source: 'DESK SCALE', label: 'MEASURED', value: `${actual.toLocaleString('en-US')} CR`,
        tag: 'revenue', bad: mismatched, clientX: e.clientX, clientY: e.clientY,
      });
      return;
    }

    const doc = this.topDocAt(world);
    if (!doc) return;

    const field = this.fieldAt(doc, world);
    if (field) {
      this.cb.onFieldSelected({ ...field, clientX: e.clientX, clientY: e.clientY });
      return;
    }
    // Start drag
    this.dragging = doc;
    this.dragOffset.set(doc.mesh.position.x - world.x, doc.mesh.position.y - world.y);
    doc.mesh.position.z = this.zTop += 0.01;
    this.renderer.domElement.style.cursor = 'grabbing';
  };

  private fieldAt(doc: DocMesh, world: THREE.Vector2): Omit<InspectSelection, 'clientX' | 'clientY'> | null {
    const localX = (world.x - (doc.mesh.position.x - doc.worldW / 2)) / doc.worldW * doc.canvasW;
    const localY = (1 - (world.y - (doc.mesh.position.y - doc.worldH / 2)) / doc.worldH) * doc.canvasH;
    if (doc.sealRect) {
      const r = doc.sealRect;
      if (localX >= r.x && localX <= r.x + r.w && localY >= r.y && localY <= r.y + r.h) {
        return {
          source: doc.spec.title, label: 'SEAL', value: doc.spec.seal ?? 'none',
          tag: 'seal', bad: doc.spec.seal !== 'authentic',
        };
      }
    }
    for (const fr of doc.fieldRects) {
      if (localX >= fr.x && localX <= fr.x + fr.w && localY >= fr.y && localY <= fr.y + fr.h) {
        return {
          source: doc.spec.title, label: fr.field.label, value: fr.field.value,
          tag: fr.field.tag, bad: fr.field.bad,
        };
      }
    }
    return null;
  }

  /** True if the world point sits over an inspectable target (face, scale readout, or a doc field/seal). */
  private isInspectableAt(world: THREE.Vector2): boolean {
    if (this.character &&
        Math.abs(world.x - this.character.position.x) < 182 &&
        Math.abs(world.y - this.character.position.y) < 209) {
      return true;
    }
    if (this.scaleReadout && this.coinWeight &&
        Math.abs(world.x - this.scaleReadout.position.x) < 130 &&
        Math.abs(world.y - this.scaleReadout.position.y) < 30) {
      return true;
    }
    const doc = this.topDocAt(world);
    return !!doc && !!this.fieldAt(doc, world);
  }

  private readonly onPointerMove = (e: PointerEvent): void => {
    const world = this.eventToWorld(e);
    if (this.dragging) {
      this.dragMoved = true;
      const doc = this.dragging;
      doc.mesh.position.x = THREE.MathUtils.clamp(world.x + this.dragOffset.x, -790, 790);
      doc.mesh.position.y = THREE.MathUtils.clamp(world.y + this.dragOffset.y, -430, 70);
      return;
    }
    this.renderer.domElement.style.cursor = this.isInspectableAt(world) ? 'zoom-in' : 'grab';
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    const world = this.eventToWorld(e);
    if (this.folder?.userData.wantsOpen) {
      this.folder.userData.wantsOpen = false;
      this.folderOpenRequested?.();
    }
    const doc = this.dragging;
    this.dragging = null;
    if (doc && this.dragMoved) {
      // Coin bag onto scale => weigh
      if (doc.spec.kind === 'coinbag' && !this.weighed && this.coinWeight && this.scaleMesh) {
        const dx = doc.mesh.position.x - this.scaleMesh.position.x;
        const dy = doc.mesh.position.y - this.scaleMesh.position.y;
        if (Math.abs(dx) < 190 && Math.abs(dy) < 190) {
          this.weighed = true;
          this.tweens.push(tweenTo(doc.mesh.position, {
            x: this.scaleMesh.position.x + 8,
            y: this.scaleMesh.position.y + 30,
          }, 0.25));
          this.cb.sfx('credit');
          this.scaleReadout = this.textSprite(
            `MEASURED: ${this.coinWeight.actual.toLocaleString('en-US')} CR`,
            260, 60, '#e0b060', 19,
          );
          this.scaleReadout.position.set(this.scaleMesh.position.x, this.scaleMesh.position.y + 165, 5);
          this.scene.add(this.scaleReadout);
          this.cb.onWeighed(this.coinWeight.actual);
        }
      } else {
        const slides = ['paperSlide1', 'paperSlide3'] as const;
        this.cb.sfx(slides[Math.floor(Math.random() * slides.length)]);
      }
    }
    this.renderer.domElement.style.cursor = this.isInspectableAt(world) ? 'zoom-in' : 'grab';
  };

  folderOpenRequested: (() => void) | null = null;

  // ---------------------------------------------------------------- frame
  resize(): void {
    const canvas = this.renderer.domElement;
    const width = Math.max(1, canvas.clientWidth);
    const height = Math.max(1, canvas.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (canvas.width !== Math.floor(width * dpr) || canvas.height !== Math.floor(height * dpr)) {
      this.renderer.setPixelRatio(dpr);
      this.renderer.setSize(width, height, false);
    }
    const aspect = width / height;
    let halfW = (STAGE_H / 2) * aspect;
    let halfH = STAGE_H / 2;
    if (halfW < STAGE_W / 2) {
      halfW = STAGE_W / 2;
      halfH = halfW / aspect;
    }
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number): void {
    // Callbacks may push new tweens mid-iteration; collect them separately.
    const active = this.tweens;
    this.tweens = [];
    const still = active.filter((t) => t.update(dt));
    this.tweens = still.concat(this.tweens);
    if (this.character) {
      this.character.position.y = CHAR_Y + Math.sin(performance.now() / 900) * 4;
    }
    if (this.shakeT > 0) {
      this.shakeT -= dt;
      const s = this.shakeT * 30;
      this.camera.position.x = (Math.random() - 0.5) * s;
      this.camera.position.y = (Math.random() - 0.5) * s;
    } else {
      this.camera.position.x = 0;
      this.camera.position.y = 0;
    }
  }

  render(): void {
    this.resize();
    this.renderer.render(this.scene, this.camera);
  }

  crisisShake(): void {
    this.shakeT = 0.5;
  }

  get characterInfo(): { portrait: string; name: string } {
    return { portrait: this.characterPortrait, name: this.characterName };
  }

  diagnostics(): Record<string, unknown> {
    const info = this.renderer.info;
    return {
      drawCalls: info.render.calls,
      triangles: info.render.triangles,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      docs: this.docs.length,
    };
  }

  dispose(): void {
    this.renderer.dispose();
  }
}

function disposeMesh(mesh: THREE.Mesh): void {
  mesh.geometry.dispose();
  const mat = mesh.material as THREE.MeshBasicMaterial;
  if (mat.map && mat.map instanceof THREE.CanvasTexture) mat.map.dispose();
  mat.dispose();
}
